import type { ToolUIRegistry } from "./tool-registry";

/**
 * Controls which built-in Magnus tools a copilot surface exposes.
 *
 * - `"all"` (default): the full Magnus experience. New tools shipped by the SDK
 *   appear automatically.
 * - `{ include }`: an allowlist. Only the named tools are exposed; newly added
 *   SDK tools do NOT appear unless explicitly added — safest for locked-down
 *   surfaces.
 * - `{ exclude }`: a denylist. Everything except the named tools is exposed;
 *   newly added SDK tools DO appear automatically.
 */
export type ToolPolicy =
  | "all"
  | { include: readonly string[] }
  | { exclude: readonly string[] };

/** Apply a {@link ToolPolicy} to a tool UI registry, returning a filtered copy. */
export function resolveToolRegistry(
  registry: ToolUIRegistry,
  policy: ToolPolicy = "all"
): ToolUIRegistry {
  if (policy === "all") {
    return registry;
  }

  const entries = Object.entries(registry);

  if ("include" in policy) {
    const allow = new Set(policy.include);
    return Object.fromEntries(entries.filter(([name]) => allow.has(name)));
  }

  const deny = new Set(policy.exclude);
  return Object.fromEntries(entries.filter(([name]) => !deny.has(name)));
}
